import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Input,
  Accordion,
  ConfirmDialog,
} from "@tellescope/ui";
import { Button } from "@tellescope/design-system";
import { StripeAccountSelector } from "./StripeAccountSelector";
import { ProductSearchRow } from "./ProductSearchRow";
import type { StripeShopProduct, StripeShopPrice } from "./stripeShopTypes";

interface StripeAccount {
  key: string;
  title: string;
}

interface ProductsQuery {
  products: StripeShopProduct[];
  isLoading: boolean;
  error: unknown;
  search: string;
  setSearch: (value: string) => void;
  loadMore: () => void;
  canLoadMore: boolean;
}

interface PricesQuery {
  prices: StripeShopPrice[];
  isLoading: boolean;
}

interface ProductPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: StripeAccount[];
  accountsLoading: boolean;
  stripeKey: string | null;
  onStripeKeyChange: (stripeKey: string) => void;
  /** Number of products currently selected — switching accounts clears them, so this drives the confirmation gate. */
  selectedProductCount: number;
  productsQuery: ProductsQuery;
  pricesQuery: PricesQuery;
  onDrillDownProductChange: (productId: string | null) => void;
  isPriceSelected: (stripePriceId: string) => boolean;
  onTogglePrice: (product: StripeShopProduct, price: StripeShopPrice) => void;
}

/**
 * Browse/search the Stripe catalog and pick products + price variants to add to the shop block.
 * The only place catalog browsing happens — already-added products are configured in their own
 * row in the persistent list behind this dialog, not here.
 *
 * Owns its own expanded-product state, independent of the product list's — the dialog shares the
 * single drillDownProductId-keyed price fetch with that list (via onDrillDownProductChange) but
 * must not visually expand a row behind this modal just because the dialog opened a different
 * product's accordion item.
 */
export const ProductPickerDialog = ({
  open,
  onOpenChange,
  accounts,
  accountsLoading,
  stripeKey,
  onStripeKeyChange,
  selectedProductCount,
  productsQuery,
  pricesQuery,
  onDrillDownProductChange,
  isPriceSelected,
  onTogglePrice,
}: ProductPickerDialogProps) => {
  const { t } = useTranslation("shop");
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const [pendingStripeKey, setPendingStripeKey] = useState<string | null>(null);

  const handleExpandedChange = (value: string) => {
    const next = value || null;
    setExpandedProductId(next);
    onDrillDownProductChange(next);
  };

  const handleStripeKeyChange = (nextStripeKey: string) => {
    if (nextStripeKey === stripeKey) return;
    // Switching accounts clears the current selection (products can't mix Stripe accounts), so
    // gate it behind a confirmation whenever there's actually something to lose — the account
    // dropdown itself stays showing the current account until the switch is confirmed.
    if (selectedProductCount > 0) {
      setPendingStripeKey(nextStripeKey);
      return;
    }
    onStripeKeyChange(nextStripeKey);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setExpandedProductId(null);
      onDrillDownProductChange(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("Add Product")}</DialogTitle>
          <DialogDescription>{t("Search your Stripe catalog and select price variants to add.")}</DialogDescription>
        </DialogHeader>

        <StripeAccountSelector
          accounts={accounts}
          isLoading={accountsLoading}
          value={stripeKey}
          onChange={handleStripeKeyChange}
        />

        {stripeKey && (
          <div className="space-y-2">
            <Input
              value={productsQuery.search}
              onChange={(e) => productsQuery.setSearch(e.target.value)}
              placeholder={t("Search products…")}
            />

            <div className="max-h-[55vh] overflow-y-auto pr-2 -mr-2">
              {productsQuery.isLoading ? (
                <p className="text-sm text-muted-foreground py-2">{t("Loading products…")}</p>
              ) : productsQuery.error ? (
                <p className="text-sm text-destructive py-2">
                  {t("Couldn't load products from Stripe. Please try again.")}
                </p>
              ) : productsQuery.products.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">{t("No products found in Stripe.")}</p>
              ) : (
                <Accordion
                  type="single"
                  collapsible
                  value={expandedProductId ?? ""}
                  onValueChange={handleExpandedChange}
                  className="space-y-1.5 pb-1.5"
                >
                  {productsQuery.products.map((product) => (
                    <ProductSearchRow
                      key={product.id}
                      product={product}
                      prices={expandedProductId === product.id ? pricesQuery.prices : []}
                      pricesLoading={expandedProductId === product.id && pricesQuery.isLoading}
                      isPriceSelected={isPriceSelected}
                      onTogglePrice={onTogglePrice}
                    />
                  ))}
                </Accordion>
              )}

              {productsQuery.canLoadMore && (
                <Button variant="outline" size="sm" className="mb-1" onClick={productsQuery.loadMore}>
                  {t("Load more")}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>

      <ConfirmDialog
        open={pendingStripeKey !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingStripeKey(null);
        }}
        variant="destructive"
        title={t("Switch Stripe account?")}
        description={t(
          "Products from different Stripe accounts can't be mixed in one shop at this time. Switching accounts will clear the {{amount}} product(s) currently selected.",
          { amount: selectedProductCount }
        )}
        confirmText={t("Switch and clear products")}
        cancelText={t("Cancel")}
        onConfirm={() => {
          if (pendingStripeKey) onStripeKeyChange(pendingStripeKey);
          setPendingStripeKey(null);
        }}
      />
    </Dialog>
  );
};
